import { LitElement, css, html } from 'lit'
import { customElement, property } from 'lit/decorators'
import { VesselRouteSelectionModel } from '../../../src/models/vessel-route-selection-model'
import '@maersk-global/mds-components-core/mc-modal'
import '../../voyage-popup/src/voyage-popup'
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons'
import { VesselPlanningModel } from '../../../src/models/vessel-planning-model'
import { vesselOwnRouteReasonPopupStyles } from './vessel-own-route-reason-popup-styles'
import { ReasonsForChoosingOwnRoute,  MandatoryReasons, desc } from './vessels-own-route-reaon-popup-type'
import { isOnline, getFormattedDateUTC } from '../../../src/utils/helpers';

// Adjust type definition to support the object's keys
type ReasonsForChoosingOwnRoute = keyof typeof ReasonsForChoosingOwnRoute

const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;

@customElement('vessel-own-route-reason-popup')
export class VesselOwnRouteReasonPopup extends LitElement {

    @property({ type: Boolean }) _shown: boolean = false
    @property({ type: String }) _planId: string = ''
    @property({ type: Boolean }) _shownSuccessPopup: boolean = false
    @property({ type: String }) private _comment: string | null = null
    @property({ type: String }) navWarningNo: string = ''
    @property({ type: String }) private _selectedReason?: ReasonsForChoosingOwnRoute

    constructor() {
        super()

        this._onSubmit = this._onSubmit.bind(this)
        this._onReasonChange = this._onReasonChange.bind(this)
        this._renderModalContent = this._renderModalContent.bind(this)
        this._renderReasonSubmitModal = this._renderReasonSubmitModal.bind(this)
        this._setPopupVisibility = this._setPopupVisibility.bind(this)
        this._handleNavWarningNoChange = this._handleNavWarningNoChange.bind(this)
        this._onClose = this._onClose.bind(this)
        this._onCancel = this._onCancel.bind(this)
    }

    private _popupVisibleHandler = (e: Event) => this._setPopupVisibility(e);
    private _lastSyncedTime = localStorage.getItem('lastSyncedTime');

    static get styles() {
        return vesselOwnRouteReasonPopupStyles
    }

    connectedCallback(): void {
        super.connectedCallback()
        VesselRouteSelectionModel!.addEventListener('popup-visible', this._popupVisibleHandler);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback()
        VesselRouteSelectionModel!.removeEventListener('popup-visible', this._popupVisibleHandler);
    }

    _setPopupVisibility(e: Event) {
        this._shown = (e as CustomEvent).detail.show
        this._planId = (e as CustomEvent).detail.plansId
    }

    render() {
        if (this._shownSuccessPopup) {
            return html`${this._renderSubmitSuccessModal()}`
        }
        return html`${this._renderReasonSubmitModal()}`
    }

    async _onSubmit() {
        const isMandatoryReason = MandatoryReasons.includes(this._selectedReason||'')
        const isCommentInvalid = !this._comment || this._comment.length < 1
        const isNavWarningReason = this._selectedReason === ReasonsForChoosingOwnRoute.NAVAREAwarnings
        const isNavWarningInvalid = isNavWarningReason && (!this.navWarningNo || this.navWarningNo.trim() === '')


        if (!this._selectedReason || (isMandatoryReason && isCommentInvalid) || isNavWarningInvalid) {
            return
        }

        if (this._planId) {
            await VesselRouteSelectionModel?.createVesselRouteSelectionNotification({
                reason: this._selectedReason,
                comment: this._comment || '',
                navWarningNo: this.navWarningNo || '',
            })
            VesselPlanningModel!._appovePlanClf()
        }
        this._shown = false
        this._shownSuccessPopup = true
    }
    

    private _onCancel() {
        this._shown = false
        VesselPlanningModel!._cancelPlanClf()
    }

    private _renderSubmitSuccessModal() {
        const showOfflineMessage = isVesselOnCloud && !isOnline();
        return html`
            <voyage-popup id="alarm-popup" ?noCloseButton = ${true} ?noHeader = ${true}
                ?shown=${this._shownSuccessPopup}>
                <div id="success-topContainer">
                    <div class="max-width-container">
                        <div class="success-innerContainer">
                            <div>
                                <span>${SvgIcons.tickMark}</span>
                            </div>
                            <div class="content">
                                <h4 class ="title">Thank you!</h4>
                                <div class="desc">You feedback helps us improve our Star Route suggestions.</div>
                                ${
                                    showOfflineMessage ? html`
                                        <div class="infoContanier offlinePlan">
                                            <div class="icon">
                                                <span class="network-status-icon"> ${SvgIcons.customCloudOffline}</span>
                                            </div>
                                            <div class="text">
                                                <div class ="offlineText">
                                                    Please note: You are currently offline. Your reason for choosing own route has been saved locally and will be
                                                    sent automatically when connection is restored after verifying the alignment to star route and fuel efficiency compared to star route.                  
                                                </div>
                                                <div class="syncText">
                                                    Last synced: ${getFormattedDateUTC(this._lastSyncedTime ? new Date(this._lastSyncedTime) : new Date())}
                                                </div>
                                            </div>
                                            
                                        </div>
                                        `: html``
                                }
                            </div>
                        </div>
                    </div>
                    </div>
                </div>
                    <div id="footer" slot="footer">
                         <mc-button  class="submit-button"  appearance="primary" variant="filled" @click=${() =>
            this._onClose()} >Close</mc-button>
                 </div>
            </voyage-popup>
        `
    }

    _onClose() {
        this._shownSuccessPopup = false
    }

    private _handleNavWarningNoChange(e: Event) {
        const target = e.target as HTMLInputElement
        this.navWarningNo = target.value
    }

    private _renderModalContent() {
        const isCommentInvalid = MandatoryReasons.includes(this._selectedReason||'') && !this._comment
        const showOfflineMessage = isVesselOnCloud && !isOnline();
        return html`
            <div class="desc">${desc}</div>
            <div class="input-space">
                <mc-select
                    label="Reason"
                    @optionselected=${this._onReasonChange}
                    ?invalid=${!this._selectedReason}>
                    <span slot="errormessage">Please select a reason</span>
                    ${this._renderReasonOptions()}
                </mc-select>
            </div>
            ${this._renderNavWarningInput()}
            <div class="comment-input">
                <mc-textarea
                    label=${'Comment'}
                    rows="4"
                    ?invalid=${isCommentInvalid}
                    @input=${this._onCommentChange}
                >
                    <span slot="errormessage">Please provide comment</span>
                    ${!MandatoryReasons.includes(this._selectedReason||'') ? html`<span
                        slot="label"> Comment (optional)</span>` : 'Comment'}
                </mc-textarea>
            </div>
            ${ showOfflineMessage ? html`
                <div class="infoContanier offlinePlan">
                    <div class="icon">
                        <span class="network-status-icon"> ${SvgIcons.customCloudOffline}</span>
                    </div>
                    <div class="text">
                        <div class ="offlineText">
                            Please note: You are currently offline. You can still record your reason for choosing own route, and it will be saved locally and sent automatically when the connection is restored after verifying the alignment to star route and fuel efficiency compared to star route.                  
                        </div>
                        <div class="syncText">
                            Last synced: ${getFormattedDateUTC(this._lastSyncedTime ? new Date(this._lastSyncedTime) : new Date())}
                        </div>
                    </div>
                    
                </div>
                `: html``
            }
        `
    }

    private _renderReasonOptions() {
        return Object.keys(ReasonsForChoosingOwnRoute).map(
            (reasonKey: keyof typeof ReasonsForChoosingOwnRoute) => html`
          <mc-option value=${ReasonsForChoosingOwnRoute[reasonKey]}>${ReasonsForChoosingOwnRoute[reasonKey]}</mc-option>
        `,
        )
    }

    private _renderNavWarningInput() {
        const isNavWarningReason = this._selectedReason === ReasonsForChoosingOwnRoute.NAVAREAwarnings
        const isNavWarningInvalid = isNavWarningReason && (!this.navWarningNo || this.navWarningNo.trim() === '')

        if (isNavWarningReason) {
            return html`
            <div class="selection">
                <div class="input-space">
                <mc-input
                    label="Nav Warning No."
                    .value=${this.navWarningNo}
                    @input=${this._handleNavWarningNoChange}
                    ?invalid=${isNavWarningInvalid}
                    required
                >
                    <span slot="errormessage">Please provide Nav Warning No.</span>
                </mc-input>
                </div>
            </div>
        `
        }

        return ''
    }

    _onCommentChange(event: Event) {
        const text = (event.target as HTMLInputElement).value.trim()
        this._comment = text
    }

    private _onReasonChange(e: Event) {
        const target = e.target as HTMLSelectElement
        this._selectedReason = target.value as ReasonsForChoosingOwnRoute
        if (this._selectedReason !== ReasonsForChoosingOwnRoute.NAVAREAwarnings) {
            this.navWarningNo = ''
        }
    }

    private _renderReasonSubmitModal() {
        return html`<mc-modal
            heading=${`Reason for choosing Own Route`}
            ?open=${this._shown}
            height="auto"
            dimension="small"
            backdropcloseactiondisabled
            escapecloseactiondisabled
            entercloseactiondisabled
            hiddenclose
        >
            ${this._renderModalContent()}
            <div class="footer">
            <mc-button
                    focusstartanchor
                    appearance="neutral"
                    variant="filled"
                    @click=${this._onCancel}
                    >Cancel</mc-button
                >
                <mc-button
                    focusstartanchor
                    appearance="primary"
                    variant="filled"
                    @click=${this._onSubmit}
                    >Submit</mc-button
                >
            </div>
        </mc-modal> `
    }
}
